import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

// Storage keys for preferences
const STORAGE_KEYS = {
  PUSH_ENABLED: 'celey_push_enabled',
  DEVICE_TOKEN: 'celey_device_token',
};

// Dev-only diagnostic logger. PUSH_DEBUG lines are noisy in production
// (they get written to iOS Console.app when reviewers attach a device),
// and a few of them include the FCM device token which we do not want to
// surface in shipping logs. NODE_ENV is replaced at build time by CRA, so
// this is a true no-op in production bundles.
const __isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
const pushLog = __isDev ? console.log.bind(console) : () => {};
const pushErr = __isDev ? console.error.bind(console) : () => {};

/**
 * Event types emitted by the push notification service
 * Components can subscribe to these for navigation
 */
export const PUSH_EVENTS = {
  NOTIFICATION_TAP: 'push_notification_tap',
};

class PushNotificationService {
  constructor() {
    this.token = null;
    this.initialized = false;
    this.isAvailable = false;
    this._listeners = new Map();
    this._checkAvailability();
  }

  /**
   * Check if push notifications are available on this platform
   */
  _checkAvailability() {
    try {
      // Push notifications only work on native platforms, not web/simulator
      this.isAvailable = Capacitor.isNativePlatform() && 
        typeof PushNotifications !== 'undefined' &&
        typeof PushNotifications.checkPermissions === 'function';
      
      pushLog('PUSH_DEBUG: isNativePlatform =', Capacitor.isNativePlatform());
      pushLog('PUSH_DEBUG: PushNotifications available =', this.isAvailable);
    } catch (e) {
      pushLog('PUSH_DEBUG: Error checking availability:', e);
      this.isAvailable = false;
    }
  }

  /**
   * Check if push notifications are supported
   */
  isSupported() {
    return this.isAvailable;
  }

  /**
   * Get stored preference for push notifications
   * @returns {boolean} Whether user has enabled push notifications
   */
  getStoredPreference() {
    const stored = localStorage.getItem(STORAGE_KEYS.PUSH_ENABLED);
    return stored === 'true';
  }

  /**
   * Tri-state preference: 'enabled' | 'disabled' | null
   *   - 'enabled'  → user explicitly turned push ON
   *   - 'disabled' → user explicitly turned push OFF in-app
   *   - null       → first-time / no choice ever made
   * The distinction matters: an OS-level permission grant is treated as
   * implicit consent ONLY when the user has not explicitly disabled.
   */
  getStoredPreferenceState() {
    const stored = localStorage.getItem(STORAGE_KEYS.PUSH_ENABLED);
    if (stored === 'true') return 'enabled';
    if (stored === 'false') return 'disabled';
    return null;
  }

  /**
   * Set stored preference for push notifications
   * @param {boolean} enabled - Whether push is enabled
   */
  setStoredPreference(enabled) {
    localStorage.setItem(STORAGE_KEYS.PUSH_ENABLED, String(enabled));
  }

  /**
   * Safely check permissions - returns null if not available
   */
  async checkPermissions() {
    if (!this.isAvailable) {
      pushLog('PUSH_DEBUG: checkPermissions skipped - not available');
      return null;
    }
    try {
      return await PushNotifications.checkPermissions();
    } catch (e) {
      pushLog('PUSH_DEBUG: checkPermissions error:', e);
      return null;
    }
  }

  /**
   * Safely request permissions - returns null if not available
   * Only call this when user explicitly enables push notifications
   */
  async requestPermissions() {
    if (!this.isAvailable) {
      pushLog('PUSH_DEBUG: requestPermissions skipped - not available');
      return null;
    }
    try {
      return await PushNotifications.requestPermissions();
    } catch (e) {
      pushLog('PUSH_DEBUG: requestPermissions error:', e);
      return null;
    }
  }

  /**
   * Subscribe to push notification events
   * @param {string} event - Event type from PUSH_EVENTS
   * @param {Function} callback - Handler function
   * @returns {Function} Unsubscribe function
   */
  subscribe(event, callback) {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set());
    }
    this._listeners.get(event).add(callback);
    
    // Return unsubscribe function
    return () => {
      this._listeners.get(event)?.delete(callback);
    };
  }

  /**
   * Emit an event to all subscribers
   * @param {string} event - Event type
   * @param {object} data - Event data
   */
  _emit(event, data) {
    const listeners = this._listeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (e) {
          pushErr('PUSH_DEBUG: Error in event listener:', e);
        }
      });
    }
  }

  /**
   * Initialize push notifications.
   * Called on app startup AND after every login/session restore.
   *
   * @param {Object} [opts]
   * @param {boolean} [opts.forceForAdmin=false]
   *   Admin-only system override. When true AND OS permission is granted,
   *   proceed with registration EVEN IF the stored preference is 'disabled'.
   *   The override is strictly session-only — the stored 'disabled' value is
   *   NEVER mutated by this path. Customer/vendor flows MUST NOT pass this
   *   flag; default `false` preserves byte-identical behavior for them.
   *   Rationale: admin users have no in-app toggle UI to flip 'disabled'
   *   back to 'enabled', which would otherwise lock them out of test-push.
   *
   * Tri-state preference logic (guardrail — do not auto-enable after explicit OFF):
   *   - 'disabled'           → user explicitly turned push OFF in-app — bail
   *   - 'enabled'            → standard registration if OS permission is granted
   *   - null (first-time)    → if OS permission is already granted, treat as
   *                            implicit consent: register and persist 'enabled'.
   *                            If OS permission is NOT granted, bail and let
   *                            the manual enable() flow request it.
   */
  async initialize({ forceForAdmin = false } = {}) {
    if (this.initialized) return true;
    if (!this.isAvailable) {
      pushLog('PUSH_DEBUG: initialize skipped - not available');
      return false;
    }

    const prefState = this.getStoredPreferenceState();
    if (prefState === 'disabled' && !forceForAdmin) {
      pushLog('PUSH_DEBUG: initialize skipped - user explicitly disabled');
      return false;
    }
    if (prefState === 'disabled' && forceForAdmin) {
      pushLog('PUSH_DEBUG: admin override — proceeding despite stored disabled');
    }

    try {
      const permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive !== 'granted') {
        // OS-level permission missing: do nothing here. The manual enable()
        // flow is the only place that should ask the user for it.
        pushLog('PUSH_DEBUG: initialize skipped - OS permission not granted (state=' + prefState + ')');
        return false;
      }

      // OS permission is granted. Set up listeners + register, regardless of
      // whether prefState is 'enabled' or null (first-time).
      await this._setupListeners();
      await PushNotifications.register();

      // Timing fix: if the native FCM token was emitted before the JS
      // 'registration' listener existed, the event was dropped. Re-send any
      // cached token through the JS path so _sendTokenToBackend runs once
      // listeners are confirmed present.
      await this.reRegisterToken();

      // First-time: persist consent so subsequent runs use the standard 'enabled' path.
      if (prefState === null) {
        this.setStoredPreference(true);
        pushLog('PUSH_DEBUG: First-time OS-grant detected — preference set to enabled');
      }

      this.initialized = true;
      pushLog('PUSH_DEBUG: Push notifications initialized successfully');
      return true;
    } catch (error) {
      pushErr('PUSH_DEBUG: Failed to initialize:', error);
      return false;
    }
  }

  /**
   * Enable push notifications - requests permissions and registers
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async enable() {
    if (!this.isAvailable) {
      return { success: false, error: 'Push notifications not available' };
    }

    try {
      // Request permissions
      const result = await PushNotifications.requestPermissions();
      
      if (result.receive !== 'granted') {
        return { success: false, error: 'Permission denied' };
      }

      // Set up listeners if not already done
      if (!this.initialized) {
        await this._setupListeners();
      }

      // Register for push
      await PushNotifications.register();

      // Store preference
      this.setStoredPreference(true);
      this.initialized = true;

      pushLog('PUSH_DEBUG: Push notifications enabled');
      return { success: true };
    } catch (error) {
      pushErr('PUSH_DEBUG: Failed to enable:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Re-register cached token with backend (call after login to ensure token is associated)
   */
  async reRegisterToken() {
    try {
      const result = await Capacitor.Plugins.CeleyPushPlugin.getCurrentToken();
      if (result?.value) {
        localStorage.setItem(STORAGE_KEYS.DEVICE_TOKEN, result.value);
        await this._sendTokenToBackend(result.value);
        return;
      }
    } catch (e) {
      pushLog("Token fetch from native failed", e);
    }

    const cachedToken = localStorage.getItem(STORAGE_KEYS.DEVICE_TOKEN);
    if (cachedToken && this.isAvailable && Capacitor.getPlatform() !== 'web') {
      pushLog('PUSH_DEBUG: Re-registering cached native token after login');
      await this._sendTokenToBackend(cachedToken);
    }
  }

  /**
   * Disable push notifications - updates preference (cannot revoke OS permissions)
   */
  disable() {
    this.setStoredPreference(false);
    pushLog('PUSH_DEBUG: Push notifications disabled (preference stored)');
    // Note: We cannot programmatically revoke OS-level permissions
    // User must go to device Settings to fully disable
  }

  /**
   * Set up notification listeners
   */
  async _setupListeners() {
    // Registration success
    await PushNotifications.addListener('registration', (token) => {
      pushLog('PUSH_DEBUG: Registration token received:', token.value?.substring(0, 20) + '...');
      this.token = token.value;
      localStorage.setItem(STORAGE_KEYS.DEVICE_TOKEN, token.value);
      this._sendTokenToBackend(token.value);
    });

    // Registration error
    await PushNotifications.addListener('registrationError', (error) => {
      pushErr('PUSH_DEBUG: Registration error:', error);
    });

    // Notification received while app is in foreground
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      // iOS native willPresent (AppDelegate UNUserNotificationCenterDelegate)
      // already displays the foreground banner via [.banner, .badge, .sound].
      // We intentionally do NOT call LocalNotifications.schedule(...) here —
      // doing so would produce a DUPLICATE banner because iOS would display
      // both the original remote push AND the JS-scheduled local notification.
      // The `_showLocalNotification` helper below is kept as dead code for
      // potential future opt-in (e.g. custom in-app banner UI), but must not
      // be invoked from this listener while willPresent shows the banner.
      pushLog('PUSH_DEBUG: Notification received in foreground:', notification);
    });

    // Notification tapped
    await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      pushLog('PUSH_DEBUG: Notification tapped:', notification);
      this._handleNotificationTap(notification);
    });

    pushLog('PUSH_DEBUG: Listeners set up');
  }

  /**
   * Send device token to backend
   */
  async _sendTokenToBackend(token) {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        pushLog('PUSH_DEBUG: No auth token, skipping backend registration');
        return;
      }

      // Map Capacitor platform to backend expected values
      const capPlatform = Capacitor.getPlatform();
      const platform = capPlatform === 'ios' ? 'ios' : 'android';

      // Only send native tokens (not web — web uses service worker subscriptions)
      if (capPlatform === 'web') {
        pushLog('PUSH_DEBUG: Web platform — native token registration skipped (uses Web Push)');
        return;
      }

      const API = process.env.REACT_APP_BACKEND_URL;
      // Feb 2026: AbortController 15s timeout so a cold-start backend cannot
      // stall this fetch indefinitely. If it times out we fall through to
      // the catch and bail gracefully — token will re-register on the next
      // session restore via initializePushNotifications().
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      let response;
      try {
        response = await fetch(`${API}/api/push/register-device`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({ token, platform }),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }

      if (response.ok) {
        pushLog('PUSH_DEBUG: Token registered with backend');
      } else {
        const err = await response.text();
        pushErr('PUSH_DEBUG: Failed to register token with backend:', err);
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        pushErr('PUSH_DEBUG: register-device timed out after 15s — will retry on next session restore');
      } else {
        pushErr('PUSH_DEBUG: Error sending token to backend:', error);
      }
    }
  }

  /**
   * Get a unique device identifier
   */
  async _getDeviceId() {
    // Use a stored ID or generate one
    let deviceId = localStorage.getItem('celey_device_id');
    if (!deviceId) {
      deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      localStorage.setItem('celey_device_id', deviceId);
    }
    return deviceId;
  }

  /**
   * Show local notification when app is in foreground.
   *
   * ⚠️ DEAD CODE — intentionally not called from `pushNotificationReceived`.
   * iOS `willPresent` (AppDelegate UNUserNotificationCenterDelegate) already
   * displays the foreground banner for remote pushes. Calling this would
   * schedule a SECOND local notification with identical content and produce
   * a duplicate banner. Kept here only as a reference / opt-in hook for
   * future in-app custom-banner experiments.
   */
  async _showLocalNotification(notification) {
    try {
      if (typeof LocalNotifications === 'undefined') return;

      await LocalNotifications.schedule({
        notifications: [{
          title: notification.title || 'New Notification',
          body: notification.body || '',
          id: Date.now(),
          extra: notification.data
        }]
      });
    } catch (error) {
      pushErr('PUSH_DEBUG: Failed to show local notification:', error);
    }
  }

  /**
   * Handle notification tap - emits event for React components to handle navigation
   * Does NOT navigate directly - components subscribe and navigate using React Router
   *
   * Resolution order for the tap target:
   *   1. data.route   — explicit deep link from backend payload (PREFERRED)
   *   2. data.url     — legacy field (kept for backward compatibility)
   *   3. type-based fallback (last-resort guess from notification type + user role)
   */
  _handleNotificationTap(notification) {
    const data = notification.notification?.data || {};
    const type = data.type;

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isVendor = user.role === 'vendor';

    // 1. Prefer explicit `route` from backend payload
    let targetRoute = typeof data.route === 'string' && data.route ? data.route : null;

    // 2. Fall back to legacy `url` field
    if (!targetRoute && typeof data.url === 'string' && data.url) {
      targetRoute = data.url;
    }

    // 3. Last-resort: derive from notification type + role
    if (!targetRoute) {
      switch (type) {
        case 'new_message':
        case 'message':
          targetRoute = isVendor
            ? '/vendor/dashboard?tab=messages'
            : '/customer-dashboard?tab=messages';
          break;
        case 'new_inquiry':
        case 'inquiry':
          targetRoute = '/vendor/dashboard?tab=inquiries';
          break;
        case 'inquiry_accepted':
        case 'proposal':
          targetRoute = isVendor
            ? '/vendor/dashboard?tab=inquiries'
            : '/customer-dashboard?tab=inquiries';
          break;
        case 'deposit_confirmed':
        case 'job_completed':
        case 'booking':
          targetRoute = isVendor
            ? '/vendor/dashboard?tab=bookings'
            : '/customer-dashboard?tab=bookings';
          break;
        case 'payment_released':
        case 'payment':
          targetRoute = '/vendor/dashboard?tab=bookings';
          break;
        default:
          targetRoute = isVendor ? '/vendor/dashboard' : '/customer-dashboard';
      }
    }

    // Emit event with navigation data - React components handle actual navigation
    this._emit(PUSH_EVENTS.NOTIFICATION_TAP, {
      type,
      data,
      targetRoute,
      notification,
    });

    pushLog('PUSH_DEBUG: Emitted notification tap event, target:', targetRoute, 'source:', data.route ? 'route' : (data.url ? 'url' : 'fallback'));
  }

  /**
   * Register for push notifications (legacy method, use enable() instead)
   */
  async register() {
    return this.enable();
  }

  /**
   * Get current token
   */
  getToken() {
    return this.token || localStorage.getItem(STORAGE_KEYS.DEVICE_TOKEN);
  }

  /**
   * Check if push is currently enabled (both preference AND permissions)
   */
  async isEnabled() {
    if (!this.getStoredPreference()) {
      return false;
    }
    
    const perms = await this.checkPermissions();
    return perms?.receive === 'granted';
  }

  /**
   * Remove all listeners and cleanup
   */
  async cleanup() {
    try {
      await PushNotifications.removeAllListeners();
    } catch (e) {
      // Ignore errors during cleanup
    }
    this.initialized = false;
    this._listeners.clear();
  }
}

// Create singleton instance
const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
